import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  message,
  Modal,
  Empty,
  Popconfirm,
  Statistic,
  Row,
  Col,
  Tabs,
  Breadcrumb,
} from 'antd';
import {
  DeleteOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  SafetyCertificateOutlined,
  StarOutlined,
  HomeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { certificationAPI } from '../services/api';
import AppLayout from '../components/AppLayout';
import dayjs from 'dayjs';

const { TabPane } = Tabs;

function MyCertifications() {
  const navigate = useNavigate();
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedCert, setSelectedCert] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  const fetchCertifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
      };

      // Add status filter if not "all"
      if (activeTab !== 'all') {
        params.status = activeTab;
      }

      const response = await certificationAPI.list(params);

      if (response.status === 'success') {
        setCertifications(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
        }));
      }
    } catch (error) {
      console.error('Error fetching certifications:', error);
      message.error('Gagal memuat data sertifikasi');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, activeTab]);

  useEffect(() => {
    fetchCertifications();
  }, [fetchCertifications]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPagination(prev => ({ ...prev, current: 1 })); // Reset to page 1
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
  };

  const handleViewPDF = (cert) => {
    // Always open in new tab (for both URL and local files)
    if (cert.file_path && (cert.file_path.startsWith('http://') || cert.file_path.startsWith('https://'))) {
      window.open(cert.file_path, '_blank');
    } else {
      // For local files, open download endpoint in new tab
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:6543'}/api/certifications/${cert.id}/download`;
      window.open(url, '_blank');
    }
    message.success('Membuka sertifikasi di tab baru');
  };

  const handleDelete = async (certId) => {
    try {
      const response = await certificationAPI.delete(certId);
      if (response.status === 'success') {
        message.success('Sertifikasi berhasil dihapus');
        fetchCertifications();
      }
    } catch (error) {
      console.error('Error deleting:', error);
      message.error('Gagal menghapus sertifikasi');
    }
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: {
        color: 'orange',
        icon: <ClockCircleOutlined />,
        text: 'Menunggu Verifikasi',
      },
      verified: {
        color: '#14B8A6',
        icon: <CheckCircleOutlined />,
        text: 'Terverifikasi',
      },
      rejected: {
        color: 'red',
        icon: <CloseCircleOutlined />,
        text: 'Ditolak',
      },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Calculate stats
  const totalCertifications = certifications.length;
  const verifiedCertifications = certifications.filter((c) => c.status === 'verified').length;
  const pendingCertifications = certifications.filter((c) => c.status === 'pending').length;
  const totalPoints = certifications.reduce((sum, c) => sum + (c.status === 'verified' ? 1 : 0), 0);

  const columns = [
    {
      title: 'Judul',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Jenis',
      dataIndex: 'type',
      key: 'type',
      width: 160,
      render: () => <Tag color="#14B8A6">Sertifikasi AI</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Poin',
      key: 'points',
      width: 80,
      align: 'center',
      render: (_, record) =>
        record.status === 'verified' ? (
          <Tag color="#14B8A6">1</Tag>
        ) : (
          <span style={{ color: '#999' }}>-</span>
        ),
    },
    {
      title: 'Tanggal Upload',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (date) => (date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'),
    },
    {
      title: 'Aksi',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewPDF(record)}
            block
          >
            View
          </Button>
          {record.status === 'pending' && (
            <Popconfirm
              title="Hapus sertifikasi?"
              description="Apakah Anda yakin ingin menghapus sertifikasi ini?"
              onConfirm={() => handleDelete(record.id)}
              okText="Ya"
              cancelText="Tidak"
            >
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                block
              >
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <AppLayout activeMenu="/certifications">
      <div className="my-certifications-page">
        {/* Breadcrumb */}
        <Breadcrumb style={{ marginBottom: 16 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Sertifikasi AI</Breadcrumb.Item>
          <Breadcrumb.Item>Sertifikasi Saya</Breadcrumb.Item>
        </Breadcrumb>

        {/* Stats Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Total Sertifikasi"
                value={totalCertifications}
                prefix={<SafetyCertificateOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Terverifikasi"
                value={verifiedCertifications}
                valueStyle={{ color: '#14B8A6' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Menunggu"
                value={pendingCertifications}
                valueStyle={{ color: '#faad14' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Total Poin"
                value={totalPoints}
                valueStyle={{ color: '#14B8A6' }}
                prefix={<StarOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Main Content Card */}
        <Card
          title={
            <Space>
              <SafetyCertificateOutlined />
              <span>Daftar Sertifikasi Saya</span>
            </Space>
          }
          extra={
            <Space>
              <Button
                icon={<InfoCircleOutlined />}
                onClick={() => navigate('/certifications/info')}
              >
                Panduan
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/certifications/upload')}
              >
                Upload Sertifikasi
              </Button>
            </Space>
          }
        >
          <Tabs activeKey={activeTab} onChange={handleTabChange} className="certifications-tabs">
            <TabPane
              tab={
                <span>
                  <SafetyCertificateOutlined /> Semua
                </span>
              }
              key="all"
            />
            <TabPane
              tab={
                <span>
                  <ClockCircleOutlined /> Menunggu Verifikasi
                </span>
              }
              key="pending"
            />
            <TabPane
              tab={
                <span>
                  <CheckCircleOutlined /> Terverifikasi
                </span>
              }
              key="verified"
            />
            <TabPane
              tab={
                <span>
                  <CloseCircleOutlined /> Ditolak
                </span>
              }
              key="rejected"
            />
          </Tabs>

          <Table
            columns={columns}
            dataSource={certifications}
            loading={loading}
            rowKey="id"
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} sertifikasi`,
              pageSizeOptions: ['10', '20', '50'],
            }}
            onChange={handleTableChange}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Belum ada sertifikasi yang diupload"
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate('/certifications/upload')}
                  >
                    Upload Sertifikasi Pertama
                  </Button>
                </Empty>
              ),
            }}
            expandable={{
              expandedRowRender: (record) => (
                <div style={{ padding: '16px', background: '#fafafa', borderRadius: '8px' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <strong>Nama File:</strong> {record.original_filename}
                    </div>
                    <div>
                      <strong>Ukuran:</strong> {formatFileSize(record.file_size)}
                    </div>
                    {record.completion_date && (
                      <div>
                        <strong>Tanggal Selesai:</strong>{' '}
                        {dayjs(record.completion_date).format('DD MMMM YYYY')}
                      </div>
                    )}
                    {record.status === 'rejected' && record.notes && (
                      <div>
                        <strong>Alasan Ditolak:</strong>
                        <div style={{ marginTop: '8px', color: '#ff4d4f' }}>{record.notes}</div>
                      </div>
                    )}
                    {record.verified_by_user && (
                      <div>
                        <strong>Diverifikasi oleh:</strong> {record.verified_by_user.email}
                        {record.verified_at && (
                          <span style={{ marginLeft: '16px', color: '#666' }}>
                            pada {dayjs(record.verified_at).format('DD/MM/YYYY HH:mm')}
                          </span>
                        )}
                      </div>
                    )}
                    {record.notes && record.status === 'verified' && (
                      <div>
                        <strong>Catatan:</strong> {record.notes}
                      </div>
                    )}
                  </Space>
                </div>
              ),
              rowExpandable: (record) =>
                record.status !== 'pending' || record.notes || record.completion_date,
            }}
          />
        </Card>

        {/* Detail Modal */}
        <Modal
          title="Detail Sertifikasi"
          open={detailModalVisible}
          onCancel={() => setDetailModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setDetailModalVisible(false)}>
              Tutup
            </Button>,
          ]}
          width={600}
        >
          {selectedCert && (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <strong>Judul:</strong>
                <div style={{ marginTop: 4 }}>{selectedCert.title}</div>
              </div>
              <div>
                <strong>Jenis:</strong>
                <div style={{ marginTop: 4 }}>{selectedCert.type}</div>
              </div>
              <div>
                <strong>Status:</strong>
                <div style={{ marginTop: 4 }}>{getStatusTag(selectedCert.status)}</div>
              </div>
              <div>
                <strong>Nama File:</strong>
                <div style={{ marginTop: 4 }}>{selectedCert.original_filename}</div>
              </div>
              <div>
                <strong>Ukuran File:</strong>
                <div style={{ marginTop: 4 }}>{formatFileSize(selectedCert.file_size)}</div>
              </div>
              {selectedCert.completion_date && (
                <div>
                  <strong>Tanggal Selesai:</strong>
                  <div style={{ marginTop: 4 }}>
                    {dayjs(selectedCert.completion_date).format('DD MMMM YYYY')}
                  </div>
                </div>
              )}
              <div>
                <strong>Tanggal Upload:</strong>
                <div style={{ marginTop: 4 }}>
                  {dayjs(selectedCert.created_at).format('DD MMMM YYYY HH:mm')}
                </div>
              </div>
              {selectedCert.verified_at && (
                <div>
                  <strong>Tanggal Verifikasi:</strong>
                  <div style={{ marginTop: 4 }}>
                    {dayjs(selectedCert.verified_at).format('DD MMMM YYYY HH:mm')}
                  </div>
                </div>
              )}
              {selectedCert.notes && (
                <div>
                  <strong>Catatan:</strong>
                  <div style={{ marginTop: 4 }}>{selectedCert.notes}</div>
                </div>
              )}
            </Space>
          )}
        </Modal>
      </div>
    </AppLayout>
  );
}

export default MyCertifications;
